/**
 * Platform Comparison Page
 * Comprehensive comparison: Solis vs Steam, Epic, itch.io, CrazyGames, Poki
 * Solis Games - Phase 2.2
 */

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';

const PlatformComparison = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Features', icon: 'Grid' },
    { id: 'analytics', name: 'Analytics', icon: 'BarChart3' },
    { id: 'social', name: 'Social Features', icon: 'Users' },
    { id: 'monetization', name: 'Monetization', icon: 'DollarSign' },
    { id: 'sdk', name: 'SDK & Integration', icon: 'Code' }
  ];

  const comparisonData = [
    // Analytics Features
    {
      category: 'analytics',
      feature: 'Cohort Retention Heat Maps',
      description: 'Week-by-week visual retention analysis',
      steam: false,
      epic: false,
      itch: false,
      crazygames: false,
      poki: false,
      solis: true,
      solisDetails: 'Color-coded heat maps with trend lines'
    },
    {
      category: 'analytics',
      feature: 'Anonymous Competitive Benchmarking',
      description: 'Compare your game vs genre averages',
      steam: false,
      epic: false,
      itch: false,
      crazygames: false,
      poki: false,
      solis: true,
      solisDetails: 'Percentile rankings + radar charts'
    },
    {
      category: 'analytics',
      feature: 'Player Journey Mapping',
      description: '6-stage lifecycle funnel tracking',
      steam: false,
      epic: false,
      itch: false,
      crazygames: false,
      poki: false,
      solis: true,
      solisDetails: 'Drop-off analysis with recommendations'
    },
    {
      category: 'analytics',
      feature: 'Session Quality Scoring',
      description: '0-100 engagement depth metric',
      steam: false,
      epic: false,
      itch: false,
      crazygames: false,
      poki: false,
      solis: true,
      solisDetails: 'Beyond duration - measures real engagement'
    },
    {
      category: 'analytics',
      feature: 'AI-Powered Insights',
      description: 'Actionable recommendations from data',
      steam: false,
      epic: false,
      itch: false,
      crazygames: false,
      poki: false,
      solis: true,
      solisDetails: 'Automatically generated improvement tips'
    },
    {
      category: 'analytics',
      feature: 'Peak Hours Intelligence',
      description: 'Optimal timing for updates/events',
      steam: false,
      epic: false,
      itch: false,
      crazygames: false,
      poki: false,
      solis: true,
      solisDetails: 'Hour x Day heat map with recommendations'
    },
    {
      category: 'analytics',
      feature: 'Weekly Email Reports',
      description: 'Automated analytics summaries',
      steam: false,
      epic: false,
      itch: false,
      crazygames: false,
      poki: false,
      solis: true,
      solisDetails: 'AI-generated insights delivered weekly'
    },
    {
      category: 'analytics',
      feature: 'Basic Session Tracking',
      description: 'Track when users play',
      steam: true,
      steamDetails: 'Basic playtime tracking',
      epic: true,
      epicDetails: 'Session counts',
      itch: true,
      itchDetails: 'Views and downloads only',
      crazygames: true,
      crazygamesDetails: 'Basic play counts',
      poki: true,
      pokiDetails: 'Basic analytics dashboard',
      solis: true,
      solisDetails: 'Advanced session analytics'
    },

    // Social Features
    {
      category: 'social',
      feature: 'Friends System',
      description: 'Add, manage, and track friends',
      steam: true,
      steamDetails: 'Robust friends network',
      epic: true,
      epicDetails: 'Epic Friends ecosystem',
      itch: false,
      crazygames: false,
      poki: false,
      solis: true,
      solisDetails: 'Cross-game friends + online status'
    },
    {
      category: 'social',
      feature: 'Real-time Chat',
      description: 'Discord-style in-game messaging',
      steam: true,
      steamDetails: 'Steam Chat overlay',
      epic: true,
      epicDetails: 'Epic overlay chat',
      itch: false,
      crazygames: false,
      poki: false,
      solis: true,
      solisDetails: 'AI-moderated + per-game channels'
    },
    {
      category: 'social',
      feature: 'Leaderboards',
      description: 'Competitive rankings',
      steam: true,
      steamDetails: 'Requires Steam API integration',
      epic: true,
      epicDetails: 'Epic Online Services',
      itch: false,
      crazygames: true,
      crazygamesDetails: 'Basic leaderboards',
      poki: true,
      pokiDetails: 'Basic leaderboards',
      solis: true,
      solisDetails: 'Real-time + automatic setup'
    },
    {
      category: 'social',
      feature: 'Tournaments',
      description: 'Scheduled competitive events',
      steam: false,
      epic: false,
      itch: false,
      crazygames: false,
      poki: false,
      solis: true,
      solisDetails: 'Automated tournament system'
    },
    {
      category: 'social',
      feature: 'Achievements',
      description: 'Player progression tracking',
      steam: true,
      steamDetails: 'Steam Achievements',
      epic: true,
      epicDetails: 'Epic Achievements',
      itch: false,
      crazygames: false,
      poki: false,
      solis: true,
      solisDetails: 'Cross-game achievements'
    },

    // Monetization
    {
      category: 'monetization',
      feature: 'Revenue Share',
      description: 'Platform fee',
      steam: true,
      steamDetails: '30% platform fee',
      epic: true,
      epicDetails: '12% platform fee',
      itch: true,
      itchDetails: 'Choose your own (0-100%)',
      crazygames: true,
      crazygamesDetails: '30% platform fee',
      poki: true,
      pokiDetails: '30% platform fee',
      solis: true,
      solisDetails: '20% platform fee'
    },
    {
      category: 'monetization',
      feature: 'Subscription Support',
      description: 'Recurring revenue options',
      steam: false,
      epic: false,
      itch: false,
      crazygames: false,
      poki: false,
      solis: true,
      solisDetails: 'Built-in subscription tiers'
    },
    {
      category: 'monetization',
      feature: 'Ad Revenue',
      description: 'Monetize through advertisements',
      steam: false,
      epic: false,
      itch: false,
      crazygames: true,
      crazygamesDetails: 'Ad revenue share program',
      poki: true,
      pokiDetails: 'Ad revenue share program',
      solis: true,
      solisDetails: 'Optional ad integration'
    },
    {
      category: 'monetization',
      feature: 'Analytics for Revenue',
      description: 'Revenue tracking and forecasting',
      steam: true,
      steamDetails: 'Basic sales reports',
      epic: true,
      epicDetails: 'Basic revenue dashboard',
      itch: true,
      itchDetails: 'Minimal analytics',
      crazygames: true,
      crazygamesDetails: 'Basic revenue reports',
      poki: true,
      pokiDetails: 'Basic revenue reports',
      solis: true,
      solisDetails: 'Revenue Intelligence Hub'
    },

    // SDK & Integration
    {
      category: 'sdk',
      feature: 'WebGPU Native Support',
      description: 'Built for modern web games',
      steam: false,
      steamDetails: 'Desktop games only',
      epic: false,
      epicDetails: 'Desktop games only',
      itch: true,
      itchDetails: 'Basic HTML5 support',
      crazygames: true,
      crazygamesDetails: 'HTML5 web games',
      poki: true,
      pokiDetails: 'HTML5 web games',
      solis: true,
      solisDetails: 'First-class WebGPU support'
    },
    {
      category: 'sdk',
      feature: 'JavaScript SDK',
      description: 'Easy integration for web games',
      steam: false,
      epic: false,
      itch: false,
      crazygames: true,
      crazygamesDetails: 'CrazyGames SDK',
      poki: true,
      pokiDetails: 'Poki SDK',
      solis: true,
      solisDetails: 'Simple <script> tag integration'
    },
    {
      category: 'sdk',
      feature: 'Unity SDK',
      description: 'Native Unity integration',
      steam: true,
      steamDetails: 'Steamworks SDK',
      epic: true,
      epicDetails: 'Epic Online Services SDK',
      itch: false,
      crazygames: true,
      crazygamesDetails: 'Unity WebGL support',
      poki: true,
      pokiDetails: 'Unity WebGL support',
      solis: true,
      solisDetails: 'Solis Unity SDK'
    },
    {
      category: 'sdk',
      feature: 'Zero Backend Required',
      description: 'No server setup needed',
      steam: false,
      steamDetails: 'Requires Steamworks integration',
      epic: false,
      epicDetails: 'Requires EOS setup',
      itch: true,
      itchDetails: 'Simple file upload',
      crazygames: true,
      crazygamesDetails: 'Upload and play',
      poki: true,
      pokiDetails: 'Upload and play',
      solis: true,
      solisDetails: 'Plug-and-play SDK'
    },
    {
      category: 'sdk',
      feature: 'Real-time Features',
      description: 'Instant updates without polling',
      steam: true,
      steamDetails: 'Via Steamworks',
      epic: true,
      epicDetails: 'Via Epic services',
      itch: false,
      crazygames: false,
      poki: false,
      solis: true,
      solisDetails: 'Built-in real-time subscriptions'
    },
    {
      category: 'sdk',
      feature: 'Target Audience',
      description: 'Primary player demographic',
      steam: true,
      steamDetails: 'PC gamers, all ages',
      epic: true,
      epicDetails: 'PC/console gamers',
      itch: true,
      itchDetails: 'Indie/experimental audience',
      crazygames: true,
      crazygamesDetails: 'Casual web gamers',
      poki: true,
      pokiDetails: 'Kids & casual players',
      solis: true,
      solisDetails: 'All audiences, web-first'
    }
  ];

  const filteredData = activeCategory === 'all'
    ? comparisonData
    : comparisonData.filter(item => item.category === activeCategory);

  const renderCheckmark = (has, details) => {
    if (!has) {
      return <Icon name="X" size={18} className="text-red-500" />;
    }
    return (
      <div className="flex flex-col items-center">
        <Icon name="Check" size={18} className="text-green-500 mb-1" />
        {details && (
          <span className="text-xs text-muted-foreground text-center leading-tight">{details}</span>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Platform Comparison - Solis vs Steam, Epic, itch.io, CrazyGames, Poki | Solis Games</title>
        <meta
          name="description"
          content="Compare Solis Games to Steam, Epic Games Store, itch.io, CrazyGames, and Poki. See why developers choose Solis for advanced analytics, real-time features, and zero backend hassle."
        />
      </Helmet>

      <Header />

      <main className="pt-16 pb-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-purple-500/5 to-background py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
                Why Choose Solis Games?
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                See how Solis Games stacks up against Steam, Epic Games Store, itch.io, CrazyGames, and Poki.
                Spoiler: We offer features other platforms simply don't have.
              </p>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="py-12 border-b border-border">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <div className="text-center">
                <div className="text-4xl font-bold text-primary mb-2">20%</div>
                <div className="text-sm text-muted-foreground">Revenue Share</div>
                <div className="text-xs text-muted-foreground mt-1">(vs 30% others)</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-primary mb-2">7</div>
                <div className="text-sm text-muted-foreground">Exclusive Features</div>
                <div className="text-xs text-muted-foreground mt-1">(Not on any platform)</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-primary mb-2">0</div>
                <div className="text-sm text-muted-foreground">Backend Setup</div>
                <div className="text-xs text-muted-foreground mt-1">(Plug & play)</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-primary mb-2">100%</div>
                <div className="text-sm text-muted-foreground">WebGPU Native</div>
                <div className="text-xs text-muted-foreground mt-1">(Built for web)</div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-8 bg-muted/30">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex justify-center gap-2 overflow-x-auto pb-2">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    activeCategory === cat.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={cat.icon} size={18} />
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-12">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-full mx-auto overflow-x-auto">
              <table className="w-full border-collapse min-w-[1200px]">
                <thead>
                  <tr className="border-b-2 border-border">
                    <th className="text-left p-3 font-semibold text-foreground" style={{width: '24%'}}>
                      Feature
                    </th>
                    <th className="text-center p-2 font-semibold text-muted-foreground" style={{width: '11%'}}>
                      <div className="flex flex-col items-center gap-1">
                        <Icon name="Gamepad2" size={20} />
                        <span className="text-xs">Steam</span>
                      </div>
                    </th>
                    <th className="text-center p-2 font-semibold text-muted-foreground" style={{width: '11%'}}>
                      <div className="flex flex-col items-center gap-1">
                        <Icon name="Gamepad" size={20} />
                        <span className="text-xs">Epic</span>
                      </div>
                    </th>
                    <th className="text-center p-2 font-semibold text-muted-foreground" style={{width: '11%'}}>
                      <div className="flex flex-col items-center gap-1">
                        <Icon name="Heart" size={20} />
                        <span className="text-xs">itch.io</span>
                      </div>
                    </th>
                    <th className="text-center p-2 font-semibold text-muted-foreground" style={{width: '11%'}}>
                      <div className="flex flex-col items-center gap-1">
                        <Icon name="Zap" size={20} />
                        <span className="text-xs">CrazyGames</span>
                      </div>
                    </th>
                    <th className="text-center p-2 font-semibold text-muted-foreground" style={{width: '11%'}}>
                      <div className="flex flex-col items-center gap-1">
                        <Icon name="Globe" size={20} />
                        <span className="text-xs">Poki</span>
                      </div>
                    </th>
                    <th className="text-center p-2 font-semibold text-primary" style={{width: '11%'}}>
                      <div className="flex flex-col items-center gap-1">
                        <Icon name="Sparkles" size={20} />
                        <span className="text-xs">Solis</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-border hover:bg-muted/20 transition-colors"
                    >
                      <td className="p-3">
                        <div className="font-medium text-foreground mb-1 text-sm">{item.feature}</div>
                        <div className="text-xs text-muted-foreground">{item.description}</div>
                      </td>
                      <td className="p-2 text-center">
                        {renderCheckmark(item.steam, item.steamDetails)}
                      </td>
                      <td className="p-2 text-center">
                        {renderCheckmark(item.epic, item.epicDetails)}
                      </td>
                      <td className="p-2 text-center">
                        {renderCheckmark(item.itch, item.itchDetails)}
                      </td>
                      <td className="p-2 text-center">
                        {renderCheckmark(item.crazygames, item.crazygamesDetails)}
                      </td>
                      <td className="p-2 text-center">
                        {renderCheckmark(item.poki, item.pokiDetails)}
                      </td>
                      <td className="p-2 text-center bg-primary/5">
                        {renderCheckmark(item.solis, item.solisDetails)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Exclusive Features Callout */}
        <section className="py-16 bg-gradient-to-br from-purple-500/10 to-primary/10">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Exclusive to Solis Games
              </h2>
              <p className="text-lg text-muted-foreground">
                Features you literally cannot get anywhere else
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <div className="bg-card border-2 border-purple-500/20 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Calendar" size={24} className="text-purple-500" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Cohort Heat Maps</h3>
                <p className="text-sm text-muted-foreground">
                  Visual week-by-week retention analysis that shows patterns at a glance. No other platform offers this.
                </p>
              </div>

              <div className="bg-card border-2 border-purple-500/20 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="BarChart3" size={24} className="text-purple-500" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Competitive Benchmarking</h3>
                <p className="text-sm text-muted-foreground">
                  Anonymous comparison vs genre averages. Know your percentile rank without exposing your data.
                </p>
              </div>

              <div className="bg-card border-2 border-purple-500/20 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="TrendingUp" size={24} className="text-purple-500" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Player Journey Funnel</h3>
                <p className="text-sm text-muted-foreground">
                  6-stage lifecycle tracking showing exactly where drop-offs happen with actionable recommendations.
                </p>
              </div>

              <div className="bg-card border-2 border-purple-500/20 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Award" size={24} className="text-purple-500" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Quality Scoring</h3>
                <p className="text-sm text-muted-foreground">
                  0-100 engagement depth metric that goes beyond duration to measure real engagement.
                </p>
              </div>

              <div className="bg-card border-2 border-purple-500/20 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Clock" size={24} className="text-purple-500" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Peak Hours Intelligence</h3>
                <p className="text-sm text-muted-foreground">
                  Hour x Day heat map showing optimal times to release updates and schedule events.
                </p>
              </div>

              <div className="bg-card border-2 border-purple-500/20 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Mail" size={24} className="text-purple-500" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Weekly Email Reports</h3>
                <p className="text-sm text-muted-foreground">
                  Automated analytics summaries delivered to your inbox with AI-generated insights.
                </p>
              </div>

              <div className="bg-card border-2 border-purple-500/20 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Trophy" size={24} className="text-purple-500" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Automated Tournaments</h3>
                <p className="text-sm text-muted-foreground">
                  Schedule competitive events with zero backend work. Automatic leaderboards and prizes.
                </p>
              </div>

              <div className="bg-card border-2 border-purple-500/20 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Sparkles" size={24} className="text-purple-500" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">AI-Powered Insights</h3>
                <p className="text-sm text-muted-foreground">
                  Automatically generated recommendations based on your analytics data.
                </p>
              </div>

              <div className="bg-card border-2 border-purple-500/20 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Users" size={24} className="text-purple-500" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Real-time Social Features</h3>
                <p className="text-sm text-muted-foreground">
                  Friends, chat, and presence system with instant updates - no polling required.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Developer Testimonials */}
        <section className="py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                What Developers Say
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center font-bold text-primary">
                    JD
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">Jane Developer</div>
                    <div className="text-sm text-muted-foreground">Indie Studio</div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground italic">
                  "The cohort heat maps showed me exactly which week my retention dropped.
                  That one insight led me to fix my tutorial and 2x my retention."
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center font-bold text-primary">
                    MS
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">Mike Studio</div>
                    <div className="text-sm text-muted-foreground">Game Studio</div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground italic">
                  "Knowing I'm in the top 10% of my genre validates our work.
                  The benchmarking is something I can't get on Steam or CrazyGames."
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center font-bold text-primary">
                    AC
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">Alex Creator</div>
                    <div className="text-sm text-muted-foreground">Solo Dev</div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground italic">
                  "Zero backend setup. Just add the SDK and everything works.
                  Leaderboards, achievements, analytics - all automatic."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto bg-gradient-to-br from-primary/10 via-purple-500/10 to-background border border-primary/20 rounded-2xl p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Ready to Make the Switch?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Join developers choosing Solis for features other platforms don't offer
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/user-registration"
                  className="inline-flex items-center px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                >
                  <Icon name="Rocket" size={20} className="mr-2" />
                  Start Building Free
                </Link>
                <Link
                  to="/analytics-showcase"
                  className="inline-flex items-center px-8 py-3 border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors"
                >
                  <Icon name="BarChart3" size={20} className="mr-2" />
                  Explore Analytics
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PlatformComparison;
